/**
 * Setup Wizard Constants
 *
 * These constants mirror the backend definitions in:
 * backend/models/hydrology_common/setup_wizard/input_sites.py
 *
 * Keep in sync with the backend when updating.
 * TODO: Have that happen automatically. I think we can cut down on this list
 * and tie this with our planned automatic type generation. Allowing duplication
 * for now to get this working!
 */

// --- Site Info Column Name Constants ---

export const SITE_NAME_COLUMN = 'name';
export const LAT_COLUMN = 'lat';
export const LON_COLUMN = 'lon';
export const OUTLET_NAME_COLUMN = 'outlet_name';
export const TOTAL_OR_LOCAL_FLOWS_COLUMN = 'total_or_local_flows';
export const REMOVE_UPSTREAM_CONNECTIVITY_COLUMN = 'remove_upstream_site_connectivity';
// Multiple upstream sites are stored in one field joined by this delimiter.
// Keep in sync with REMOVE_UPSTREAM_CONNECTIVITY_DELIMITER in input_sites.py.
export const REMOVE_UPSTREAM_CONNECTIVITY_DELIMITER = ';';
export const SUBTRACT_UPSTREAM_AREA_COLUMN = 'subtract_upstream_area';
export const REGULATED_OR_NATURAL_COLUMN = 'regulated_or_natural';
export const ARE_OBSERVATIONS_AVAILABLE_COLUMN = 'are_observations_available';
export const PAID_FOR_COLUMN = 'paid_for';
export const GAUGE_TYPE_COLUMN = 'gauge_type';
export const GAUGE_ID_COLUMN = 'gauge_id';

// Backward-compatible aliases
export const REMOTE_GAUGE_ID_COLUMN = GAUGE_ID_COLUMN;
export const SOURCE_OF_OBSERVATIONS_COLUMN = GAUGE_TYPE_COLUMN;

export const SITE_INFO_COLUMNS = [
  SITE_NAME_COLUMN,
  LAT_COLUMN,
  LON_COLUMN,
  OUTLET_NAME_COLUMN,
  TOTAL_OR_LOCAL_FLOWS_COLUMN,
  REMOVE_UPSTREAM_CONNECTIVITY_COLUMN,
  SUBTRACT_UPSTREAM_AREA_COLUMN,
  REGULATED_OR_NATURAL_COLUMN,
  ARE_OBSERVATIONS_AVAILABLE_COLUMN,
  PAID_FOR_COLUMN,
  GAUGE_TYPE_COLUMN,
  GAUGE_ID_COLUMN,
] as const;

export type SiteInfoColumn = (typeof SITE_INFO_COLUMNS)[number];

export const UNIT_OPTIONS = ['US', 'Metric'] as const;
export type UnitOption = (typeof UNIT_OPTIONS)[number];

export const FLOW_OPTIONS = ['Total', 'Local'] as const;
export const REGULATION_OPTIONS = ['Regulated', 'Natural'] as const;
export const SOURCE_OF_OBSERVATIONS_OPTIONS = [
  'Private',
  'usgs',
  'ca-dwr',
  'usace',
  'eccc',
  'usbr',
  'opg-gauge',
] as const;

export const REQUESTED_FORECASTS_OPTIONS = [
  'short-term',
  'seasonal',
  'seasonal-extended',
  'short-term+seasonal',
  'short-term+seasonal-extended',
] as const;

export type RequestedForecast = (typeof REQUESTED_FORECASTS_OPTIONS)[number];

/** Minimum widths per column type to prevent narrow inputs */
export const COLUMN_MIN_WIDTHS: Partial<Record<SiteInfoColumn, string>> = {
  [SITE_NAME_COLUMN]: 'min-w-[10rem]',
  [LAT_COLUMN]: 'min-w-[7rem]',
  [LON_COLUMN]: 'min-w-[7rem]',
  [OUTLET_NAME_COLUMN]: 'min-w-[8rem]',
  [GAUGE_ID_COLUMN]: 'min-w-[8rem]',
};

/** Columns that should render as checkboxes */
export const BOOLEAN_COLUMNS = new Set([
  SUBTRACT_UPSTREAM_AREA_COLUMN,
  ARE_OBSERVATIONS_AVAILABLE_COLUMN,
]);

/** Columns that should render as number inputs */
export const NUMBER_COLUMNS = new Set([LAT_COLUMN, LON_COLUMN]);

/** Columns with dropdown select options */
export const SELECT_OPTIONS: Record<string, readonly string[]> = {
  [TOTAL_OR_LOCAL_FLOWS_COLUMN]: FLOW_OPTIONS,
  [REGULATED_OR_NATURAL_COLUMN]: REGULATION_OPTIONS,
  [GAUGE_TYPE_COLUMN]: SOURCE_OF_OBSERVATIONS_OPTIONS,
  [PAID_FOR_COLUMN]: REQUESTED_FORECASTS_OPTIONS,
};

/** Row type for site info table - keys are column names, values are the appropriate types */
export interface SiteInfoRow {
  [key: string]: unknown;
  name: string;
  lat: number | null;
  lon: number | null;
  outlet_name: string | null;
  total_or_local_flows: 'Total' | 'Local' | null;
  // Upstream forecast point(s) above which flow should not be included (for
  // local-flow sites); blank for total-flow sites. One or more site names joined
  // by REMOVE_UPSTREAM_CONNECTIVITY_DELIMITER. Read as node names by the backend
  // delineation step, so each must be a defined site name, not a boolean.
  remove_upstream_site_connectivity: string | null;
  subtract_upstream_area: boolean;
  regulated_or_natural: 'Regulated' | 'Natural' | null;
  are_observations_available: boolean;
  paid_for: RequestedForecast | null;
  gauge_type: (typeof SOURCE_OF_OBSERVATIONS_OPTIONS)[number] | null;
  gauge_id: string | null;
}

// --- Observations Tab Constants ---

export const DATA_SOURCE_TYPE_COLUMN = 'data_source_type';
export const DATA_FREQUENCY_COLUMN = 'data_frequency';
export const DATA_UNITS_COLUMN = 'data_units';
export const OBSERVATION_COLUMN_NAME_COLUMN = 'observation_column_name';
export const DATETIME_COLUMN_NAME_COLUMN = 'datetime_column_name';
export const SAMPLE_TIMING_COLUMN = 'sample_timing';
export const TYPE_OF_FLOW_COLUMN = 'type_of_flow';
export const TIMEZONE_OF_OBSERVATIONS_COLUMN = 'timezone_of_observations';
export const MISSING_DATA_VALUE_COLUMN = 'missing_data_value';

export const OBSERVATION_METADATA_COLUMNS = [
  DATA_SOURCE_TYPE_COLUMN,
  DATA_FREQUENCY_COLUMN,
  DATA_UNITS_COLUMN,
  OBSERVATION_COLUMN_NAME_COLUMN,
  DATETIME_COLUMN_NAME_COLUMN,
  SAMPLE_TIMING_COLUMN,
  TYPE_OF_FLOW_COLUMN,
  TIMEZONE_OF_OBSERVATIONS_COLUMN,
  MISSING_DATA_VALUE_COLUMN,
] as const;

export type ObsMetadataColumn = (typeof OBSERVATION_METADATA_COLUMNS)[number];

export const OBS_DATA_SOURCE_TYPE_OPTIONS = ['USGS', 'Private', 'Other Agency'] as const;
export const OBS_DATA_FREQUENCY_OPTIONS = ['15-min', 'Hourly', 'Daily'] as const;
export const OBS_DATA_UNITS_OPTIONS = ['CMS', 'CFS'] as const;
export const OBS_SAMPLE_TIMING_OPTIONS = ['Start of Period', 'End of Period'] as const;
export const OBS_FLOW_TYPE_OPTIONS = ['flow', 'inflow', 'outflow', 'inflow-outflow'] as const;

export const OBS_SELECT_OPTIONS: Record<string, readonly string[]> = {
  [DATA_SOURCE_TYPE_COLUMN]: OBS_DATA_SOURCE_TYPE_OPTIONS,
  [DATA_FREQUENCY_COLUMN]: OBS_DATA_FREQUENCY_OPTIONS,
  [DATA_UNITS_COLUMN]: OBS_DATA_UNITS_OPTIONS,
  [SAMPLE_TIMING_COLUMN]: OBS_SAMPLE_TIMING_OPTIONS,
  [TYPE_OF_FLOW_COLUMN]: OBS_FLOW_TYPE_OPTIONS,
};

/** Row type for observation processing table */
export interface ObsProcessingRow {
  [key: string]: unknown;
  name: string;
  data_source_type: string | null;
  data_frequency: (typeof OBS_DATA_FREQUENCY_OPTIONS)[number] | null;
  data_units: (typeof OBS_DATA_UNITS_OPTIONS)[number] | null;
  observation_column_name: string | null;
  datetime_column_name: string | null;
  sample_timing: (typeof OBS_SAMPLE_TIMING_OPTIONS)[number] | null;
  type_of_flow: (typeof OBS_FLOW_TYPE_OPTIONS)[number] | null;
  timezone_of_observations: string | null;
  missing_data_value: string | number | null;
}
